import { useState } from 'react';
import type { Contractor } from '../shared/api';
import { Banner, Button, Card, Field, Input, Pill } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { PERMISSION_LABELS } from '../lib/format';
import { useSession } from '../lib/session';
import { ADDRESS_HINT, looksLikeFullAddress } from '../lib/address';
import ExtensionPrompt from '../components/ExtensionPrompt';
import LogoUpload from '../components/LogoUpload';

const NOTIFICATION_EVENTS = [
  ['Deadline approaching', 'NTO and lien deadlines at 7, 3 and 1 days out'],
  ['Certified mail delivered', 'When a recipient signs for a notice'],
  ['Invoice overdue', 'When payment terms lapse'],
  ['Approval requested', 'When a field user queues a high-stakes action'],
  ['Mail balance low', 'When the wallet drops below two letters'],
] as const;

export default function Settings() {
  const { contractor, pendingOfficeInvite, refresh } = useSession();
  const homeowner = contractor?.accountRole === 'homeowner';
  const personOk = !!contractor?.personVerifiedAt;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [joining, setJoining] = useState(false);
  const [form, setForm] = useState({
    companyName: contractor?.companyName ?? '',
    licenseNumber: contractor?.licenseNumber ?? '',
    phone: contractor?.phone ?? '',
    address: contractor?.address ?? '',
  });

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.address.trim() && !looksLikeFullAddress(form.address)) {
      setSaved(false);
      setError(`Enter your full mailing address. ${ADDRESS_HINT}.`);
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Name and contact details never need identity — they print on paperwork
      // the contractor sends themselves. Claiming a license does (it locks the
      // number to this account), so before verification the hidden license
      // field is not sent at all; sending it as null would clear it.
      const canClaim = homeowner || personOk;
      await api.put<{ contractor: Contractor }>(
        '/api/me',
        canClaim
          ? {
              companyName: form.companyName,
              licenseNumber: form.licenseNumber || null,
              phone: form.phone || null,
              address: form.address || null,
            }
          : { companyName: form.companyName, phone: form.phone || null, address: form.address || null },
      );
      await refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your settings');
    } finally {
      setSaving(false);
    }
  };

  const startScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>('/api/me/identity-session', {
        returnUrl: `${window.location.origin}/settings`,
      });
      window.location.assign(url);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start identity verification');
      setScanning(false);
    }
  };

  const joinOffice = async () => {
    if (!personOk) {
      await startScan();
      return;
    }
    setJoining(true);
    setError(null);
    try {
      await api.post('/api/me/office-join', {});
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not join this office');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-ink">{homeowner ? 'Account settings' : 'Company settings'}</h1>
        <p className="text-sm text-muted">
          {homeowner
            ? 'How we reach you about filings on your properties.'
            : 'This is what prints on every notice, waiver and lien we generate for you.'}
        </p>
      </div>

      {saved && <Banner tone="ok">Settings saved.</Banner>}
      {error && <Banner tone="urgent">{error}</Banner>}

      {pendingOfficeInvite && (
        <Card title="Join this company">
          <p className="text-sm text-muted">
            You were invited to {pendingOfficeInvite.companyName}. Email is not identity. Scan a government ID with a
            live selfie, then join this office. Role is still chosen on each job.
          </p>
          <div className="mt-4">
            <Button disabled={scanning || joining} onClick={() => void joinOffice()}>
              {personOk
                ? joining
                  ? 'Joining…'
                  : 'Join this office'
                : scanning
                  ? 'Opening scan…'
                  : 'Verify who you are'}
            </Button>
          </div>
        </Card>
      )}

      {!homeowner && !personOk && !pendingOfficeInvite && (
        <Card title="Who you are">
          <p className="text-sm text-muted">
            Verify with a driver’s license or passport and a live selfie to let LienGuardian send documents in your
            company’s name — certified mail, emailed waivers, counsel referrals — and to claim your license number.
            Preparing documents to print and send yourself doesn’t need it.
          </p>
          <div className="mt-4">
            <Button disabled={scanning} onClick={() => void startScan()}>
              {scanning ? 'Opening scan…' : 'Verify who you are'}
            </Button>
          </div>
        </Card>
      )}

      <Card title={homeowner ? 'You' : 'Company'}>
        <form onSubmit={save} className="space-y-4">
          <Field
            label={homeowner ? 'Name' : 'Company name'}
            hint={
              !homeowner && !personOk
                ? 'Printed on the notices and waivers you send yourself. LienGuardian sends documents in this name only after you verify who you are.'
                : undefined
            }
          >
            <Input
              required
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.target.value })}
            />
          </Field>
          {!homeowner && personOk && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="License number"
              hint="Asserting a Florida license claims it on this account. That is not verification — email is not identity."
            >
              <Input value={form.licenseNumber} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })} placeholder="EC1300456" />
            </Field>
            <p className="self-end text-sm text-muted">Role is chosen on each job. This account is not a sub, GC, or prime.</p>
          </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Mailing address" hint={`Printed on every notice as the lienor's address. ${ADDRESS_HINT}.`}>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
          </div>
          <div className="flex items-center justify-between border-t border-border pt-4">
            <p className="text-xs text-dim">
              Signed in as {contractor?.email} · {contractor ? PERMISSION_LABELS[contractor.permissionLevel] : ''}
            </p>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </Card>

      {!homeowner && (
      <Card title="Company logo">
        <LogoUpload logoUrl={contractor?.logoUrl} onChange={refresh} />
      </Card>
      )}

      {!homeowner && (
        <Card title="Chrome extension">
          <ExtensionPrompt variant="card" />
        </Card>
      )}

      <Card title="Notifications">
        {/* TODO: Run 4 — per-event delivery preferences (push, email, SMS). */}
        <div className="mb-4 space-y-2 text-sm text-muted">
          <p>
            <strong className="text-ink">Deadline reminders are emailed</strong> to the account owner and every manager or
            admin on the account, between 7am and 8pm Eastern:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Notice to Owner: 10, 5, 2 and 1 days before, on the day, and the day after if it was missed.</li>
            <li>Claim of Lien: 30, 14, 7, 3 and 1 days before, on the day, and the day after.</li>
            <li>Lien enforcement, once a lien is recorded: 90, 60, 30, 14, 7 and 1 days before.</li>
          </ul>
          <p>They stop as soon as the step is recorded as done, and for archived or closed jobs. Choosing per-event channels comes later.</p>
        </div>
        <ul className="space-y-2">
          {NOTIFICATION_EVENTS.map(([label, detail]) => (
            <li key={label} className="flex items-center justify-between gap-4 rounded-lg border border-border bg-surface-alt/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-ink">{label}</p>
                <p className="text-xs text-muted">{detail}</p>
              </div>
              <Pill>Coming soon</Pill>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
